<template>
  <ModalLoading :isLoading="isLoading" />
  <div class="login-page">

    <!-- Panel izquierdo — branding -->
    <div class="brand-panel">
      <div class="brand-content">
        <div class="brand-wordmark">
          <span class="brand-pos">POS</span><span class="brand-wave">WAVE</span>
        </div>
        <div class="brand-by-wrap">
          <span class="brand-by-text">by</span>
          <img
            src="https://perdomoyasociados.com/wp-content/uploads/2023/09/logo_perdomo_2023_dorado-768x150.png"
            alt="Perdomo y Asociados"
            class="brand-by-logo"
          />
        </div>
        <div class="brand-divider"></div>
        <p class="brand-sub">Gestiona ventas, inventario y reportes desde un solo lugar.</p>
      </div>
      <p class="brand-copy">© 2025 Perdomo y Asociados</p>
    </div>

    <!-- Panel derecho — formulario -->
    <div class="form-panel">
      <div class="form-box">

        <!-- ESPERANDO APROBACIÓN DEL ADMIN -->
        <template v-if="esperandoAprobacion">
          <div class="sp-waiting">
            <div class="sp-waiting-icon">
              <i class="bi bi-shield-lock-fill"></i>
            </div>
            <h2 class="sp-waiting-title">Solicitando acceso</h2>
            <p class="sp-waiting-sub">
              Esperando que el administrador apruebe la conexión de soporte.
            </p>
            <div class="sp-waiting-agente" v-if="agenteParam">
              <i class="bi bi-person-badge-fill"></i> {{ agenteParam }}
            </div>
            <div class="sp-waiting-empresa" v-if="empresaParam">
              <i class="bi bi-building"></i> {{ empresaParam }}
              <template v-if="sucursalParam"> · {{ sucursalParam }}</template>
            </div>
            <div class="sp-waiting-timer" :class="{ urgent: countdownSeg < 60 }">
              <i class="bi bi-clock"></i>
              {{ Math.floor(countdownSeg / 60) }}:{{ String(countdownSeg % 60).padStart(2, '0') }}
            </div>
            <div class="sp-waiting-dots">
              <span></span><span></span><span></span>
            </div>
            <div v-if="estadoSolicitud === 'rejected'" class="sp-waiting-result rejected">
              <i class="bi bi-x-circle-fill"></i> Acceso rechazado por el administrador.
            </div>
            <div v-else-if="estadoSolicitud === 'expired'" class="sp-waiting-result expired">
              <i class="bi bi-hourglass-bottom"></i> El administrador no respondió a tiempo.
            </div>
          </div>
        </template>

        <!-- LOGIN -->
        <template v-else-if="!isRecoveringPassword">
          <h1 class="form-title">Bienvenido</h1>
          <p class="form-sub">Ingresa tus credenciales para continuar</p>

          <form @submit.prevent="login" class="form">
            <div class="field">
              <label class="field-label">Usuario</label>
              <div class="field-input-wrap">
                <i class="bi bi-person field-icon"></i>
                <input type="text" v-model="username" placeholder="Nombre de usuario" required autocomplete="username" />
              </div>
            </div>

            <div class="field">
              <label class="field-label">Contraseña</label>
              <div class="field-input-wrap">
                <i class="bi bi-lock field-icon"></i>
                <input :type="showPassword ? 'text' : 'password'" v-model="password" placeholder="••••••••" required autocomplete="current-password" />
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'" class="field-toggle" @click="togglePasswordVisibility"></i>
              </div>
            </div>

            <div class="form-options">
              <label class="checkbox-label">
                <input type="checkbox" v-model="rememberMe" />
                <span>Recordar sesión</span>
              </label>
              <a href="#" class="link-forgot" @click.prevent="togglePasswordRecovery">¿Olvidaste la contraseña?</a>
            </div>

            <button type="submit" class="btn-submit" :disabled="isLoading">
              <span>Ingresar</span>
              <i class="bi bi-arrow-right-short"></i>
            </button>
          </form>
        </template>

        <!-- RECUPERAR -->
        <template v-else-if="isRecoveringPassword">
          <h1 class="form-title">Recuperar acceso</h1>
          <p class="form-sub">Te enviaremos una contraseña temporal a tu correo</p>

          <form @submit.prevent="recoverPassword" class="form">
            <div class="field">
              <label class="field-label">Correo electrónico</label>
              <div class="field-input-wrap">
                <i class="bi bi-envelope field-icon"></i>
                <input type="email" v-model="recoveryEmail" placeholder="tu@correo.com" required />
              </div>
            </div>

            <button type="submit" class="btn-submit" :disabled="isLoading">
              <span>Enviar correo</span>
              <i class="bi bi-send"></i>
            </button>
          </form>

          <a href="#" class="link-back" @click.prevent="togglePasswordRecovery">
            <i class="bi bi-arrow-left"></i> Volver al inicio de sesión
          </a>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import ModalLoading from "@/components/ModalLoading.vue";
import { useToast } from "vue-toastification"; // Importación para el popup
const { getApi } = require("../../config/getApiUrl.js");
const { connectSocket } = require("@/services/socket");
import { setPageTitle } from "@/components/pageMetadata";

export default {
  components: {
    ModalLoading,
  },
  data() {
    return {
      isLoading: false,
      username: "",
      password: "",
      rememberMe: false,
      showPassword: false,
      isRecoveringPassword: false,
      recoveryEmail: "",
      agenteParam: "",
      empresaParam: "",
      sucursalParam: "",
      // Aprobación soporte
      esperandoAprobacion: false,
      sessionIdSolicitud: null,
      countdownSeg: 300,
      estadoSolicitud: null,
      countdownTimer: null,
      pollingTimer: null,
    };
  },
  computed: {
    role() {
      return localStorage.getItem("role"); // Define role como una propiedad computada
    },
  },
  mounted() {
    setPageTitle("Inicio de Sesión");
    // Leer params aquí — antes de cualquier navegación del router
    const params = new URLSearchParams(window.location.search);
    const u = params.get('u');
    const p = params.get('p');
    this.agenteParam   = params.get('agente') || '';
    this.empresaParam  = params.get('emp')    || '';
    this.sucursalParam = params.get('suc')    || '';
    if (u && p) {
      this.username = u;
      this.password = p;
      // Si es usuario soporte (sp_*), pedir aprobación al admin antes de entrar
      if (u.startsWith('sp_')) {
        this.$nextTick(() => this.solicitarAccesoSoporte());
      } else {
        this.$nextTick(() => this.login());
      }
    }
  },
  methods: {
    async solicitarAccesoSoporte() {
      try {
        const res = await fetch(`${getApi()}/soporte-acceso/solicitar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username:      this.username,
            agente_perdomo: this.agenteParam,
            empresa:       this.empresaParam,
            sucursal:      this.sucursalParam,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Error al solicitar acceso');

        this.sessionIdSolicitud = data.session_id;
        this.esperandoAprobacion = true;
        this.countdownSeg = 300;

        // Countdown visual
        this.countdownTimer = setInterval(() => {
          this.countdownSeg--;
          if (this.countdownSeg <= 0) this._limpiarTimers();
        }, 1000);

        // Polling del estado cada 3 segundos
        this.pollingTimer = setInterval(() => this._verificarEstado(), 3000);

      } catch (err) {
        const toast = useToast();
        toast.error('No se pudo enviar la solicitud de acceso: ' + err.message);
      }
    },

    async _verificarEstado() {
      if (!this.sessionIdSolicitud) return;
      try {
        const res = await fetch(`${getApi()}/soporte-acceso/estado/${this.sessionIdSolicitud}`);
        const { estado } = await res.json();

        if (estado === 'approved') {
          this._limpiarTimers();
          // Acceso aprobado — hacer login normalmente
          this.esperandoAprobacion = false;
          await this.login();
        } else if (estado === 'rejected' || estado === 'expired' || estado === 'not_found') {
          this._limpiarTimers();
          this.estadoSolicitud = estado === 'not_found' ? 'expired' : estado;
        }
      } catch { /* red — ignorar, el polling reintentará */ }
    },

    _limpiarTimers() {
      clearInterval(this.countdownTimer);
      clearInterval(this.pollingTimer);
    },

    async login() {
      const toast = useToast();
      try {
        this.isLoading = true;

        const response = await fetch(`${getApi()}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: this.username,
            password: this.password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          const darkPref = localStorage.getItem('isDarkMode');
          localStorage.clear();
          if (darkPref !== null) localStorage.setItem('isDarkMode', darkPref);
          localStorage.setItem("auth", data.token);
          localStorage.setItem("role", data.role);
          if (data.nombre_usuario) localStorage.setItem("username", data.nombre_usuario);
          localStorage.setItem("user_permisos", JSON.stringify(data.permisos || null));
          // Guardar fecha de vencimiento y estado de suscripción para el guard de plan en el router
          if (data.fecha_vencimiento) {
            localStorage.setItem("fecha_vencimiento", data.fecha_vencimiento);
          } else {
            localStorage.removeItem("fecha_vencimiento");
          }
          if (data.estado_suscripcion) {
            localStorage.setItem("estado_suscripcion", data.estado_suscripcion);
          } else {
            localStorage.removeItem("estado_suscripcion");
          }
          // Si la empresa tiene un pago en revisión, restaurar la flag de comprobante pendiente
          // para que el router guard permita el acceso al sistema inmediatamente
          const estadosConPago = ['pago_en_revision', 'aprobado_autenticidad'];
          if (estadosConPago.includes(data.estado_suscripcion)) {
            localStorage.setItem('comprobante_pendiente', '1');
          } else {
            localStorage.removeItem('comprobante_pendiente');
          }
          window.dispatchEvent(new Event("roleChange"));

          // Cargar config de la empresa (plan_config + nombre para la sesión soporte)
          let empresaNombre = this.empresaParam || '';
          try {
            const cfgRes = await fetch(`${getApi()}/empresa/config`, {
              headers: { Authorization: `Bearer ${data.token}` }
            });
            if (cfgRes.ok) {
              const cfgData = await cfgRes.json();
              if (cfgData.empresa?.plan_config) {
                localStorage.setItem('empresa_plan', JSON.stringify(cfgData.empresa.plan_config));
              }
              if (cfgData.empresa?.nombre) {
                empresaNombre = cfgData.empresa.nombre;
              }
            }
          } catch { /* plan fetch — ignore */ }

          // Iniciar rastreo de sesión soporte si el username empieza con sp_
          if (data.nombre_usuario?.startsWith('sp_')) {
            const rolNombre = { 1: 'Administrador', 2: 'Gerente', 3: 'Cajero', 4: 'CEO' };
            sessionStorage.setItem('soporte_session', JSON.stringify({
              session_id: `sp_${Date.now()}`,
              username: data.nombre_usuario,
              rol: data.role,
              nombre_rol: rolNombre[data.role] || `Rol ${data.role}`,
              agente_perdomo: this.agenteParam || '',
              empresa:        empresaNombre    || 'Sin identificar',
              sucursal:       this.sucursalParam || (data.role == 4 ? '(acceso CEO empresa)' : ''),
              inicio: new Date().toISOString(),
              paginas: [],
              notas: '',
            }));
          }

          connectSocket();
          this.$nextTick(() => { this.$emit("auth-change"); });
          this.isLoading = false;

          // Primer login con contraseña temporal → forzar cambio de contraseña
          if (data.primer_login) {
            localStorage.setItem('primer_login', '1');
            await this.$router.push("/primer-acceso");
          } else if (data.role === "3") {
            await this.$router.push("/ventas");
          } else {
            await this.$router.push("/home");
          }
        } else {
          this.isLoading = false;
          // Aquí manejamos específicamente el caso de usuario inhabilitado
          if (response.status === 403) {
            toast.error("Usuario inhabilitado", {
              timeout: 5000,
            });
          } else {
            toast.error(data.message, {
              timeout: 5000,
            });
          }
        }
      } catch (error) {
        this.isLoading = false;
        console.error("Error:", error);
        toast.error("Error de red o servidor.", {
          timeout: 5000,
        });
      }
    },
    async recoverPassword() {
      const toast = useToast();
      try {
        this.isLoading = true;

        const response = await fetch(`${getApi()}/recuperar`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: this.recoveryEmail }),
        });

        const data = await response.json();

        if (response.ok) {
          toast.success("Correo enviado con la contraseña temporal.", {
            timeout: 5000,
          });
          this.isRecoveringPassword = false; // Volver a la vista de login
          this.recoveryEmail = ""; // Limpiar el campo
        } else {
          if (response.status === 404) {
            toast.error("Usuario no encontrado", {
              timeout: 5000,
            });
          } else {
            toast.error(data.message, {
              timeout: 5000,
            });
          }
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Error de red o servidor.", {
          timeout: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    togglePasswordRecovery() {
      this.isRecoveringPassword = !this.isRecoveringPassword;
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    changeFavicon(iconPath) {
      const link =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "icon";
      link.href = iconPath;
      document.getElementsByTagName("head")[0].appendChild(link);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap");

* { font-family: "Montserrat", sans-serif; box-sizing: border-box; margin: 0; padding: 0; }

/* ─── Layout split ─── */
.login-page {
  display: flex;
  width: 100vw;
  height: 100vh;
  background: #111;
}

/* ─── Pantalla espera aprobación ─── */
.sp-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 8px 0;
}
.sp-waiting-icon {
  font-size: 2.8rem;
  color: #c09d62;
  animation: sp-pulse 2s ease-in-out infinite;
}
@keyframes sp-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .6; transform: scale(.92); }
}
.sp-waiting-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e8e4dc;
  margin: 0;
}
.sp-waiting-sub {
  font-size: .82rem;
  color: #6b7280;
  max-width: 260px;
  line-height: 1.5;
}
.sp-waiting-agente,
.sp-waiting-empresa {
  font-size: .8rem;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sp-waiting-agente i,
.sp-waiting-empresa i { color: #c09d62; }
.sp-waiting-timer {
  font-size: 1.6rem;
  font-weight: 700;
  color: #c09d62;
  letter-spacing: .05em;
  font-variant-numeric: tabular-nums;
  margin-top: 4px;
}
.sp-waiting-timer i { font-size: 1.1rem; margin-right: 6px; }
.sp-waiting-timer.urgent { color: #ef4444; }
.sp-waiting-dots {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}
.sp-waiting-dots span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #c09d62;
  animation: sp-dots .9s ease-in-out infinite;
}
.sp-waiting-dots span:nth-child(2) { animation-delay: .15s; }
.sp-waiting-dots span:nth-child(3) { animation-delay: .3s; }
@keyframes sp-dots {
  0%, 80%, 100% { transform: scale(.6); opacity: .4; }
  40% { transform: scale(1); opacity: 1; }
}
.sp-waiting-result {
  margin-top: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: .82rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sp-waiting-result.rejected { background: rgba(239,68,68,.12); color: #ef4444; border: 1px solid rgba(239,68,68,.25); }
.sp-waiting-result.expired  { background: rgba(234,179,8,.10); color: #ca8a04; border: 1px solid rgba(234,179,8,.2); }

/* ─── Panel izquierdo ─── */
.brand-panel {
  flex: 1;
  background: linear-gradient(145deg, #1a1510 0%, #111 60%, #0d0d0d 100%);
  border-right: 1px solid rgba(192,157,98,.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 60px 48px 36px;
  position: relative;
  overflow: hidden;
}

.brand-panel::before {
  content: '';
  position: absolute;
  top: -120px; left: -120px;
  width: 420px; height: 420px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(192,157,98,.10) 0%, transparent 70%);
  pointer-events: none;
}

.brand-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: auto;
  margin-bottom: auto;
  padding-bottom: 40px;
}

.brand-wordmark {
  display: flex;
  align-items: baseline;
  gap: 0;
  line-height: 1;
}
.brand-pos {
  font-size: 3.2rem;
  font-weight: 800;
  color: #c09d62;
  letter-spacing: -.02em;
}
.brand-wave {
  font-size: 3.2rem;
  font-weight: 300;
  color: rgba(255,255,255,.75);
  letter-spacing: -.02em;
}

.brand-by-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.brand-by-text {
  font-size: .72rem;
  font-weight: 400;
  color: rgba(255,255,255,.3);
  font-style: italic;
}
.brand-by-logo {
  width: 130px;
  object-fit: contain;
  opacity: .75;
}

.brand-tagline {
  font-size: .8rem;
  font-weight: 600;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: #c09d62;
  opacity: .9;
}

.brand-divider {
  width: 48px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #c09d62, transparent);
  border-radius: 2px;
}

.brand-sub {
  font-size: .78rem;
  color: rgba(255,255,255,.35);
  text-align: center;
  line-height: 1.7;
  max-width: 240px;
}

.brand-copy {
  font-size: .7rem;
  color: rgba(255,255,255,.2);
  letter-spacing: .05em;
}

/* ─── Panel derecho ─── */
.form-panel {
  width: 480px;
  flex-shrink: 0;
  background: #161616;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
  min-height: 100vh;
}

.form-box {
  width: 100%;
  max-width: 360px;
  animation: slideUp .4s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}

.form-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
}

.form-sub {
  font-size: .8rem;
  color: rgba(255,255,255,.4);
  margin-bottom: 36px;
}

/* ─── Form ─── */
.form { display: flex; flex-direction: column; gap: 20px; }

.field { display: flex; flex-direction: column; gap: 7px; }

.field-label {
  font-size: .72rem;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(255,255,255,.45);
}

.field-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.field-icon {
  position: absolute;
  left: 14px;
  color: rgba(192,157,98,.6);
  font-size: .95rem;
  pointer-events: none;
}

.field-toggle {
  position: absolute;
  right: 14px;
  color: rgba(255,255,255,.3);
  font-size: .9rem;
  cursor: pointer;
  transition: color .2s;
}
.field-toggle:hover { color: #c09d62; }

.field-input-wrap input {
  width: 100%;
  background: #1e1e1e;
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 10px;
  padding: 13px 42px;
  font-size: .875rem;
  color: #fff;
  font-family: "Montserrat", sans-serif;
  transition: border-color .2s, box-shadow .2s;
}
.field-input-wrap input::placeholder { color: rgba(255,255,255,.2); }
.field-input-wrap input:focus {
  outline: none;
  border-color: rgba(192,157,98,.5);
  box-shadow: 0 0 0 3px rgba(192,157,98,.08);
}

/* Autofill */
.field-input-wrap input:-webkit-autofill,
.field-input-wrap input:-webkit-autofill:hover,
.field-input-wrap input:-webkit-autofill:focus {
  -webkit-text-fill-color: #fff;
  -webkit-box-shadow: 0 0 0px 1000px #1e1e1e inset;
  caret-color: #fff;
}

/* ─── Form options ─── */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -4px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: .75rem;
  color: rgba(255,255,255,.4);
  cursor: pointer;
  user-select: none;
}
.checkbox-label input[type="checkbox"] { accent-color: #c09d62; width: 14px; height: 14px; }

.link-forgot {
  font-size: .73rem;
  color: rgba(192,157,98,.7);
  text-decoration: none;
  transition: color .2s;
}
.link-forgot:hover { color: #c09d62; }

/* ─── Submit ─── */
.btn-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  margin-top: 8px;
  background: linear-gradient(135deg, #c09d62, #a8864e);
  border: none;
  border-radius: 10px;
  color: #111;
  font-size: .9rem;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  letter-spacing: .04em;
  cursor: pointer;
  transition: opacity .2s, transform .15s;
}
.btn-submit i { font-size: 1.2rem; }
.btn-submit:hover { opacity: .9; transform: translateY(-1px); }
.btn-submit:active { transform: translateY(0); }
.btn-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }

/* ─── Volver link ─── */
.link-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 24px;
  font-size: .78rem;
  color: rgba(255,255,255,.35);
  text-decoration: none;
  transition: color .2s;
}
.link-back:hover { color: #c09d62; }

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .brand-panel { display: none; }
  .form-panel  { width: 100%; padding: 40px 28px; }
}

:deep(.modal-loading) {
  position: fixed;
  z-index: 9999;
  background-color: rgba(0,0,0,.7);
}
</style>
